/**
 * Member portal journeys — pure helpers over API data.
 * Journeys and submissions come only from /api/member_tracker (src/DAL/tracker);
 * nothing here reads or writes journey data in the browser.
 */

/** Key prefixes the old browser-only demo used. Cleared so stale demo rows never linger. */
const LEGACY_DEMO_KEY_PREFIXES = [
  "gymrite_member_trackers",
  "gymrite_member_submissions",
  "gymrite_public_join_",
];

export const TRACKER_KINDS = {
  STANDARD: "standard",
  JOURNEY: "journey",
};

/** List buckets on member portal */
export const TRACKER_LIST_SECTIONS = {
  MY: "my",
  NEW: "new",
  LEFT: "left",
};

/** Member self-left a journey (status change — history stays viewable). */
export const isLeftTracker = (tracker) => tracker?.membership === "left";

/** Reasons a member can pick when leaving a journey. */
export const LEAVE_REASONS = [
  { value: "not_right_fit", label: "Not the right fit" },
  { value: "injury_health", label: "Injury or health" },
  { value: "no_time", label: "No time" },
  { value: "other", label: "Other" },
];

export const PREVIEW_LIMIT = 3;

const PRIMARY = "var(--portal-theme-primary, #04abbb)";

/** Remove journey demo data a previous build seeded into this browser. */
export const clearLegacyJourneyDemoStorage = () => {
  try {
    const stale = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && LEGACY_DEMO_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        stale.push(key);
      }
    }
    stale.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage unavailable (private mode / blocked) — nothing to clean up.
  }
};

/** My = enrolled / working / completed. New = available, not started. */
export const isMyTracker = (tracker) => {
  if (!tracker) return false;
  if (tracker.membership === "available") return false;
  if (isLeftTracker(tracker)) return false;
  if (
    tracker.membership === "active" ||
    tracker.membership === "completed"
  ) {
    return true;
  }
  return (tracker.submissionsCount || 0) > 0 || Boolean(tracker.startedAt);
};

export const groupMemberTrackers = (trackers = []) => {
  const all = (trackers || []).map((tracker) => ({
    tracker,
    meta: getTrackerListMeta(tracker),
  }));
  const my = all.filter(({ tracker }) => isMyTracker(tracker));
  const left = all.filter(({ tracker }) => isLeftTracker(tracker));
  const neu = all.filter(
    ({ tracker }) => !isMyTracker(tracker) && !isLeftTracker(tracker)
  );
  return { my, new: neu, left, all };
};

/** Normalise answer list for display (array of {label,value} or object map). */
export const getSubmissionAnswerRows = (submission) => {
  if (!submission) return [];
  if (Array.isArray(submission.answers) && submission.answers.length) {
    return submission.answers
      .map((item) => {
        if (!item) return null;
        if (typeof item === "object" && (item.label || item.value != null)) {
          const value = Array.isArray(item.value)
            ? item.value.join(", ")
            : item.value;
          return {
            label: item.label || "Answer",
            value:
              value == null || value === ""
                ? "—"
                : String(value),
          };
        }
        return null;
      })
      .filter(Boolean);
  }
  if (submission.answers && typeof submission.answers === "object") {
    return Object.entries(submission.answers).map(([label, value]) => ({
      label,
      value: Array.isArray(value)
        ? value.join(", ")
        : value == null || value === ""
          ? "—"
          : String(value),
    }));
  }
  return [];
};

/** Days until a form unlocks after journey start (baseline submit). */
export const resolveFormUnlockDays = (form, index = 0) => {
  if (form?.unlockDays != null && form.unlockDays !== "") {
    const n = Number(form.unlockDays);
    if (!Number.isNaN(n) && n >= 0) return n;
  }
  const nameMatch = String(form?.name || "").match(/(\d+)\s*[\-_ ]?\s*day/i);
  if (nameMatch) return Number(nameMatch[1]);
  if (
    index === 0 ||
    form?.role === "first_submission" ||
    form?.unlockType === "after_submit"
  ) {
    return 0;
  }
  return null;
};

export const getJourneyStartAt = (tracker, submissions = []) => {
  const sorted = [...(submissions || [])].sort(
    (a, b) =>
      new Date(a.submittedAt || 0).getTime() -
      new Date(b.submittedAt || 0).getTime()
  );
  return (
    sorted[0]?.submittedAt ||
    tracker?.startedAt ||
    tracker?.membership?.startedAt ||
    null
  );
};

const startOfLocalDay = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (value, days) => {
  const d = startOfLocalDay(value);
  if (!d) return null;
  d.setDate(d.getDate() + Number(days || 0));
  return d;
};

export const getJourneySteps = (tracker, submissions = []) => {
  const forms = Array.isArray(tracker?.forms) ? tracker.forms : [];
  const doneIds = new Set(submissions.map((s) => s.formId).filter(Boolean));
  const startAt = getJourneyStartAt(tracker, submissions);
  const today = startOfLocalDay(new Date());
  let foundNext = false;

  return forms.map((form, index) => {
    const done = doneIds.has(form.id);
    const unlockDays = resolveFormUnlockDays(form, index);
    const isBaseline =
      index === 0 ||
      form.role === "first_submission" ||
      unlockDays === 0;

    let unlockAt = null;
    let unlocked = true;
    if (!isBaseline && unlockDays != null) {
      if (!startAt) {
        unlocked = false;
      } else {
        unlockAt = addDays(startAt, unlockDays);
        unlocked = Boolean(today && unlockAt && today.getTime() >= unlockAt.getTime());
      }
    }

    let state = "upcoming";
    if (done) state = "done";
    else if (!foundNext) {
      state = unlocked ? "current" : "locked";
      foundNext = true;
    }

    const dueLabel =
      form.dueLabel ||
      (unlockDays != null && unlockDays > 0 ? `Day ${unlockDays}` : "") ||
      (isBaseline ? "Start" : "");

    return {
      ...form,
      index,
      state,
      unlocked,
      unlockDays,
      unlockAt: unlockAt ? unlockAt.toISOString() : null,
      dueLabel,
      submission:
        submissions.find((s) => s.formId === form.id) || null,
    };
  });
};

/**
 * The list API (GET /api/member_tracker) sends per-journey totals, not
 * submission rows. Members can only answer the current step, so finished
 * forms are always the leading ones: rebuild them from the API progressPercent
 * (done forms / total forms). submittedAt stays null so the start date comes
 * from the API startedAt.
 */
const getCompletedFormsFromListItem = (tracker) => {
  const forms = Array.isArray(tracker?.forms) ? tracker.forms : [];
  const percent = Number(tracker?.progressPercent) || 0;
  const doneCount = Math.min(
    forms.length,
    Math.max(0, Math.round((percent / 100) * forms.length))
  );
  return forms
    .slice(0, doneCount)
    .map((form) => ({ formId: form.id, submittedAt: null }));
};

/**
 * Card / header meta for one journey.
 * Detail screens pass the member's own rows as tracker._memberSubmissions
 * (GET /api/member_tracker/:id); list rows use the API totals.
 */
export const getTrackerListMeta = (tracker) => {
  const apiSubs = Array.isArray(tracker?._memberSubmissions)
    ? tracker._memberSubmissions
    : null;
  const steps = getJourneySteps(
    tracker,
    apiSubs || getCompletedFormsFromListItem(tracker)
  );
  const doneCount = steps.filter((s) => s.state === "done").length;
  const current = steps.find((s) => s.state === "current") || null;
  const locked = steps.find((s) => s.state === "locked") || null;
  const progress =
    steps.length > 0
      ? Math.round((doneCount / steps.length) * 100)
      : tracker.progressPercent || 0;
  const nextGate = current || locked || null;
  const isLeft = isLeftTracker(tracker);
  return {
    isLeft,
    leftAt: isLeft ? tracker.leftAt || null : null,
    statusLabel: isLeft ? "Left" : tracker.statusName || "",
    submissionsCount: apiSubs
      ? apiSubs.length
      : tracker.submissionsCount || 0,
    lastSubmittedAt:
      apiSubs?.[0]?.submittedAt || tracker.lastSubmittedAt || null,
    doneCount,
    totalSteps: steps.length,
    currentStep: current,
    lockedStep: locked,
    canSubmit: Boolean(current),
    nextReviewAt:
      nextGate?.unlockAt ||
      tracker.nextReviewAt ||
      null,
    progress: progress || tracker.progressPercent || 0,
    // Password-protected and not yet unlocked by this member (API list flag).
    isPasswordLocked: tracker.locked === true,
    isJourney:
      tracker.kind === TRACKER_KINDS.JOURNEY ||
      tracker.kind === "journey" ||
      steps.length > 1,
  };
};

export { PRIMARY };
